import React, { useState } from "react";
import { Pencil, Check, X, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

// Inline caption editor for a single album media, shown in the fullscreen theater.
// The author (or a moderator/super_admin) can edit or add a caption after publishing.
export const EditableCaption = ({ postId, mediaIndex, caption = "", canEdit = false, dark = false, onSaved, testId = "media-caption", placeholder }) => {
  const { t } = useApp();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(caption || "");
  const [saving, setSaving] = useState(false);

  const start = () => { setValue(caption || ""); setEditing(true); };
  const cancel = () => { setEditing(false); setValue(caption || ""); };
  const save = async () => {
    if (saving) return;
    const v = value.trim().slice(0, 200);
    setSaving(true);
    try {
      const { data } = await api.patch(`/posts/${postId}/media/${mediaIndex}/caption`, { caption: v });
      onSaved?.(data.caption ?? v);
      setEditing(false);
      toast.success(t("composer.caption_saved", "Légende enregistrée"));
    } catch (e) {
      toast.error(e?.friendlyMessage || "Erreur");
    } finally { setSaving(false); }
  };

  const txt = dark ? "text-white/90" : "";
  const muted = dark ? "text-white/70" : "text-[color:var(--yalola-text-muted)]";

  if (editing) {
    return (
      <div className="flex items-center gap-1.5 w-full" data-testid={`${testId}-editor`}>
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { e.stopPropagation(); cancel(); } }}
          maxLength={200}
          placeholder={placeholder || t("composer.caption_placeholder", "Ajouter une légende…")}
          className="flex-1 min-w-0 rounded-lg px-2.5 py-1.5 text-sm outline-none border"
          style={dark
            ? { borderColor: "rgba(255,255,255,0.25)", background: "rgba(0,0,0,0.35)", color: "white" }
            : { borderColor: "var(--yalola-border)", color: "var(--yalola-text)" }}
          data-testid={`${testId}-input`}
        />
        <button onClick={save} disabled={saving} className="h-8 w-8 shrink-0 grid place-items-center rounded-full text-white disabled:opacity-60" style={{ background: "var(--yalola-primary)" }} data-testid={`${testId}-save`}><Check size={16} /></button>
        <button onClick={cancel} disabled={saving} className={`h-8 w-8 shrink-0 grid place-items-center rounded-full ${dark ? "bg-white/15 text-white" : "bg-black/10"}`} data-testid={`${testId}-cancel`}><X size={16} /></button>
      </div>
    );
  }

  if (caption) {
    return (
      <div className="flex items-start gap-1.5 w-full" data-testid={`${testId}-view`}>
        <span className={`text-sm whitespace-pre-wrap flex-1 min-w-0 ${txt}`} data-testid={`${testId}-text`}>{caption}</span>
        {canEdit && (
          <button onClick={start} className={`shrink-0 h-7 w-7 grid place-items-center rounded-full hover:bg-black/10 ${muted}`} title={t("common.edit", "Modifier")} data-testid={`${testId}-edit`}><Pencil size={14} /></button>
        )}
      </div>
    );
  }

  if (canEdit) {
    return (
      <button onClick={start} className={`flex items-center gap-1.5 text-xs font-medium ${muted} hover:opacity-80`} data-testid={`${testId}-add`}>
        <Plus size={14} /> {t("composer.caption_add", "Ajouter une légende")}
      </button>
    );
  }
  return null;
};

export default EditableCaption;
